// "Our Process" — chat-timeline redesigned to match the Photoshop reference.
import React from "react";
import { LINKS } from "../data/site";

const FOUNDER_AV = "/assets/brand/NEW%20DP%20F.png";
const CLIENT_AV = "/assets/process/client-resfeezy.jpg";

// ─────────────────────────────────────────────────────────────────────────────
// Step pill — SF Pro Display Regular, theme purple number, bigger on desktop
// ─────────────────────────────────────────────────────────────────────────────
function StepPill({ n, title }) {
  return (
    <div
      className="inline-flex items-center gap-2 rounded-full"
      style={{
        padding: "clamp(9px, 0.85vw, 14px) clamp(20px, 2.2vw, 34px)",
        border: "1px solid rgba(139,92,246,0.32)",
        backgroundColor: "rgba(20,22,47,0.55)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        boxShadow: "0 8px 24px rgba(0,0,0,0.28)",
      }}
    >
      <span
        style={{
          fontFamily: "'SF Pro Display',sans-serif",
          fontWeight: 700,
          fontSize: "clamp(15px, 1.45vw, 26px)",
          color: "#8B5CF6",
          letterSpacing: "-0.005em",
        }}
      >
        Step {n}
      </span>
      <span
        style={{
          color: "rgba(255,255,255,0.55)",
          fontFamily: "'SF Pro Display',sans-serif",
          fontWeight: 400,
          fontSize: "clamp(15px, 1.45vw, 26px)",
        }}
      >
        :
      </span>
      <span
        style={{
          fontFamily: "'SF Pro Display',sans-serif",
          fontWeight: 400,
          fontSize: "clamp(15px, 1.45vw, 26px)",
          color: "#FFFFFF",
          letterSpacing: "-0.005em",
        }}
      >
        {title}
      </span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Chat bubble — white with dark text, avatars/badges outside the bubble.
// ─────────────────────────────────────────────────────────────────────────────
function ClientBubble({ children, maxW = 520, className = "" }) {
  return (
    <div className={`flex items-end gap-2 md:gap-3 ${className}`}>
      <img
        src={CLIENT_AV}
        alt="Client"
        className="shrink-0 w-9 h-9 md:w-11 md:h-11 rounded-full object-cover ring-2 ring-white/10"
      />
      <div
        className="relative rounded-[22px] px-4 py-3 md:px-5 md:py-4"
        style={{
          maxWidth: `${maxW}px`,
          backgroundColor: "#FFFFFF",
          color: "#1a1b2e",
          boxShadow:
            "0 12px 30px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.6)",
        }}
      >
        {children}
      </div>
    </div>
  );
}

function FounderBubble({ children, maxW = 520, className = "" }) {
  return (
    <div className={`flex items-end gap-2 md:gap-3 ${className}`}>
      <div
        className="relative rounded-[22px] px-4 py-3 md:px-5 md:py-4"
        style={{
          maxWidth: `${maxW}px`,
          backgroundColor: "#FFFFFF",
          color: "#1a1b2e",
          boxShadow:
            "0 12px 30px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.6)",
        }}
      >
        {children}
      </div>
      <img
        src={FOUNDER_AV}
        alt="Clickory founder"
        className="shrink-0 w-9 h-9 md:w-11 md:h-11 rounded-full object-cover ring-2 ring-white/10"
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Clean, minimal, modern arrow connector between steps.
// A dashed elegant curve ending with a small solid triangular arrowhead.
// direction: "r" = curve leans right; "l" = mirrored (leans left)
// ─────────────────────────────────────────────────────────────────────────────
function Connector({ direction = "r" }) {
  const mirror = direction === "l";
  return (
    <div className="flex justify-center my-3 md:my-5" aria-hidden="true">
      <svg
        width="86"
        height="76"
        viewBox="0 0 86 76"
        style={mirror ? { transform: "scaleX(-1)" } : undefined}
      >
        {/* Elegant dashed curve */}
        <path
          d="M10 8 Q 84 14, 46 55"
          stroke="rgba(196,181,253,0.92)"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeDasharray="3 5"
          fill="none"
        />
        {/* Small solid triangular arrowhead — clean & modern */}
        <path
          d="M40 55 L 46 66 L 52 55 Z"
          fill="rgba(196,181,253,0.92)"
          stroke="none"
        />
      </svg>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
export default function OurProcess() {
  return (
    <section id="process" className="relative py-6 md:py-14">
      <div className="max-w-4xl mx-auto px-5 md:px-8">
        {/* Heading */}
        <div className="text-center">
          <h2
            className="reveal text-transparent bg-clip-text"
            style={{
              fontFamily: "'Clash Display',sans-serif",
              fontWeight: 500,
              fontSize: "clamp(46px, 8vw, 96px)",
              lineHeight: 0.95,
              letterSpacing: "-0.02em",
              backgroundImage:
                "linear-gradient(180deg,#EDE9FE 0%,#C4B5FD 50%,#8B5CF6 100%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              margin: 0,
            }}
          >
            Our Process
          </h2>
          {/* Uploaded wavy purple underline */}
          <img
            src="/assets/images/wavy-draw.png"
            alt=""
            aria-hidden="true"
            className="reveal mx-auto mt-2 md:mt-3"
            style={{
              width: "clamp(140px, 22vw, 280px)",
              height: "auto",
              filter: "drop-shadow(0 6px 16px rgba(139,92,246,0.35))",
            }}
          />
        </div>

        <div className="mt-10 md:mt-16 space-y-3 md:space-y-4">
          {/* ─── Step 01 ─── */}
          <div className="reveal text-center">
            <StepPill n="01" title="Clear Brief & Direction" />
          </div>
          <div className="reveal pt-4 md:pt-6 flex justify-start pr-12 md:pr-0">
            <ClientBubble maxW={480}>
              <p
                style={{
                  fontFamily: "'SF Pro Display',sans-serif",
                  fontWeight: 400,
                  fontSize: "clamp(13px, 0.95vw, 15px)",
                  lineHeight: 1.55,
                }}
              >
                Hey!
                <br />
                I need a thumbnail for my upcoming video.
                <br />
                Title: NETFLIX Decides What I EAT For 24 Hours!
                <br />
                <br />
                Basically, I&apos;ll spend 24 hours letting Netflix randomly
                decide what I eat. I&apos;ll randomly pick a movie or series, and
                the first time I see food in the movie/episode, that&apos;s what I
                have to eat.
                <br />
                <br />
                The whole concept is based on randomness, so I don&apos;t choose
                the food myself.
              </p>
            </ClientBubble>
          </div>

          <Connector direction="r" />

          {/* ─── Step 02 ─── */}
          <div className="reveal text-center">
            <StepPill n="02" title="Idea Exploration & Sketching" />
          </div>
          <div className="reveal pt-4 md:pt-6 flex justify-end">
            <div
              className="relative rounded-[22px] p-3 md:p-4"
              style={{
                maxWidth: 500,
                backgroundColor: "#0B0A1F",
                border: "1px solid rgba(255,255,255,0.08)",
                boxShadow: "0 14px 34px rgba(0,0,0,0.45)",
              }}
            >
              <img
                src="/assets/process/netflix-moodboard.jpg"
                alt="Idea exploration board"
                className="w-full rounded-[12px] block"
              />
            </div>
          </div>

          <Connector direction="l" />

          {/* ─── Step 03 ─── */}
          <div className="reveal text-center">
            <StepPill n="03" title="Concept Presentation & Selection" />
          </div>
          <div className="reveal pt-4 md:pt-6 flex justify-end pl-12 md:pl-0">
            <FounderBubble maxW={420}>
              <div className="grid grid-cols-2 gap-2">
                <img
                  src="/assets/process/netflix-sketch-b.jpg"
                  alt="Concept 1"
                  className="rounded-lg w-full h-auto"
                />
                <img
                  src="/assets/process/netflix-sketch-d.jpg"
                  alt="Concept 2"
                  className="rounded-lg w-full h-auto"
                />
              </div>
              <p
                className="mt-3"
                style={{
                  fontFamily: "'SF Pro Display',sans-serif",
                  fontWeight: 400,
                  fontSize: "clamp(12px, 0.9vw, 14px)",
                  lineHeight: 1.5,
                  color: "#1a1b2e",
                }}
              >
                I&apos;ve prepared Two concepts for your review
                <br />
                Looking forward to your feedback.
              </p>
            </FounderBubble>
          </div>
          <div className="reveal pt-4 md:pt-6 flex justify-start pr-12 md:pr-0">
            <ClientBubble maxW={420}>
              <img
                src="/assets/process/netflix-sketch-b.jpg"
                alt="Chosen concept"
                className="rounded-lg mb-3 w-full h-auto"
              />
              <p
                style={{
                  fontFamily: "'SF Pro Display',sans-serif",
                  fontWeight: 400,
                  fontSize: "clamp(13px, 0.95vw, 15px)",
                  lineHeight: 1.5,
                }}
              >
                Bro, this looks amazing! <span className="align-middle">😍</span>{" "}
                Let&apos;s finalize this version.
              </p>
            </ClientBubble>
          </div>

          <Connector direction="r" />

          {/* ─── Step 04 ─── */}
          <div className="reveal text-center">
            <StepPill n="04" title="Final Design & Refinement" />
          </div>
          <div className="reveal pt-4 md:pt-6 flex justify-end pl-12 md:pl-0">
            <FounderBubble maxW={480}>
              <img
                src="/assets/process/netflix-final.jpg"
                alt="Final thumbnail"
                className="rounded-lg mb-3 w-full h-auto"
              />
              <p
                style={{
                  fontFamily: "'SF Pro Display',sans-serif",
                  fontWeight: 400,
                  fontSize: "clamp(12px, 0.9vw, 14px)",
                  lineHeight: 1.55,
                  color: "#1a1b2e",
                }}
              >
                Hi bro, here&apos;s the thumbnail.
                <br />
                Please have a look and let me know your thoughts! If there&apos;s
                anything you&apos;d like to adjust or tweak, feel free to share
                your feedback - I&apos;ll be happy to revise it
              </p>
            </FounderBubble>
          </div>
          <div className="reveal pt-4 md:pt-6 flex justify-start">
            <ClientBubble maxW={420}>
              <p
                style={{
                  fontFamily: "'SF Pro Display',sans-serif",
                  fontWeight: 400,
                  fontSize: "clamp(13px, 0.95vw, 15px)",
                  lineHeight: 1.5,
                }}
              >
                Bro, you absolutely nailed it!{" "}
                <span className="align-middle">🔥</span>
                <br />I love how it turned out.
              </p>
            </ClientBubble>
          </div>

          {/* ─── Start Your Project — premium glassy button ─── */}
          <div className="reveal pt-10 md:pt-14 flex justify-center">
            <a
              href={LINKS.bookProject}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center justify-center gap-3 rounded-full"
              style={{
                padding: "16px 34px",
                background:
                  "linear-gradient(135deg, rgba(79,70,229,0.95) 0%, rgba(139,92,246,0.95) 100%)",
                color: "#FFFFFF",
                fontFamily: "'SF Pro Display',sans-serif",
                fontWeight: 400,
                fontSize: "clamp(16px, 1.2vw, 20px)",
                letterSpacing: "0.005em",
                border: "1px solid rgba(255,255,255,0.18)",
                boxShadow:
                  "0 18px 44px rgba(79,70,229,0.55), inset 0 1px 0 rgba(255,255,255,0.28)",
                backdropFilter: "blur(10px)",
                WebkitBackdropFilter: "blur(10px)",
                transition:
                  "transform 0.25s ease, box-shadow 0.25s ease, background 0.25s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow =
                  "0 24px 54px rgba(79,70,229,0.65), inset 0 1px 0 rgba(255,255,255,0.32)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow =
                  "0 18px 44px rgba(79,70,229,0.55), inset 0 1px 0 rgba(255,255,255,0.28)";
              }}
            >
              <svg
                width="1em"
                height="1em"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <line x1="7" y1="17" x2="17" y2="7" />
                <polyline points="7 7 17 7 17 17" />
              </svg>
              Start Your Project
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
